// The record a knowledge base page answers for, and the places that name it:
// the answers URL, the page it returns to, the API read and the analytics
// properties.

import type { KnowledgeBaseTag } from "hooks/useKnowledgeBaseAPI";

export type KnowledgeBaseSubject =
  { kind: "case"; id: string } | { kind: "policy"; id: string };

/** The subject the answers route names, or null when it names neither. A
 * route names one record; the case wins if a caller ever passes both. */
export function knowledgeBaseSubjectFromParams(params: {
  caseId?: string;
  policyId?: string;
}): KnowledgeBaseSubject | null {
  if (params.caseId) return { kind: "case", id: params.caseId };
  if (params.policyId) return { kind: "policy", id: params.policyId };
  return null;
}

function subjectPath(subject: KnowledgeBaseSubject): string {
  const id = encodeURIComponent(subject.id);
  return subject.kind === "case" ? `/cases/${id}` : `/policies/${id}`;
}

export function answersPath(subject: KnowledgeBaseSubject): string {
  return `${subjectPath(subject)}/answers`;
}

/** Where the answers page leads back to. */
export function answersBackLink(subject: KnowledgeBaseSubject): {
  to: string;
  label: string;
} {
  return {
    to: subjectPath(subject),
    label:
      subject.kind === "case"
        ? "Back to your arrangement"
        : "Back to your plan",
  };
}

/** The API route for the subject's answers. The route doubles as the SWR
 * cache key, so each subject and tag filter is its own entry. */
export function knowledgeBaseEntriesRoute(
  subject: KnowledgeBaseSubject,
  tags?: readonly KnowledgeBaseTag[],
): string {
  const query = new URLSearchParams({ [`${subject.kind}_id`]: subject.id });
  if (tags && tags.length > 0) query.set("tags", tags.join(","));
  return `/knowledge-base/entries?${query.toString()}`;
}

/** The analytics property that names the subject. */
export function knowledgeBaseSubjectProperties(
  subject: KnowledgeBaseSubject,
): Record<string, string> {
  return { [`${subject.kind}_id`]: subject.id };
}
